/**
 * Distant / overview canopy surface (issue #56, LOD "far" regime). The whole
 * AOI is thousands of hectares of OSM woodland across 800+ polygons — far too
 * much to realise as individual trees. So every area polygon (forest/wood/
 * scrub) is drawn cheaply as a tinted fill surface at treetop height, merged
 * into ONE mesh (one draw call). From the boot overview all forest reads as
 * forest at near-zero cost, and up close the near-camera instanced trees
 * (vegetation.ts) take over.
 *
 * Two things this layer must get right (issue #56 regression fix):
 *
 *   1. DRAPE, don't float. The fill is NOT a flat sheet at one centroid height
 *      (that floats a dark tilted ceiling over sloped / large woods). Each
 *      polygon is subdivided (drapePolygon) so every vertex gets its own ground
 *      sample and the sheet follows the terrain contour at treetop height,
 *      sitting ON the wood. Elevation still comes ONLY from the collider
 *      sampleGround (hard constraint #6) — never invented.
 *
 *   2. Hide under the near trees. The canopy is the DISTANT LOD; within the
 *      VegetationLayer near radius the real instanced trees ARE the canopy, so
 *      a polygon fully inside that radius contributes NO fill geometry and one
 *      straddling it crossfades (per-vertex alpha) across the boundary — you
 *      never stand under a floating sheet, and it never pops.
 *
 * Purely cosmetic scene furniture — NO bearing on any LOS result (hard
 * constraint #1, guarded by vegetation.guard.test.ts).
 *
 * Perf contract mirrors RoadWallsLayer / the instanced-tree grounding
 * (issue #17): a drape vertex is raycast only while unresolved, cached once
 * resolved (a tile unloading never un-builds a canopy patch), update() spends
 * at most maxSamples raycasts per call and reports whether work remains, and a
 * vertex is attempted at most once per tile generation. The layer stays ONE
 * merged mesh with a bounded triangle count (drapePolygon caps per-polygon
 * subdivision).
 */
import { BufferAttribute, BufferGeometry, Color, DoubleSide, Group, Mesh, MeshStandardMaterial } from 'three';
import type { GroundSampler } from '../camera/groundLock';
import type { LandcoverFeature } from '../core/manifest';
import {
  classCanopy,
  canopyFadeAlpha,
  drapePolygon,
  landcoverRingToLocal,
  ringBounds,
  simplifyRing,
  triangulateXZ,
  type CanopyClassStyle,
  type RingBounds,
} from './vegetationPlacement';
import type { LocalXZ } from '../core/coordinates';

/** Decimate densely-sampled OSM woodland boundaries to ~this spacing (m) before
 * draping, so the many closely-spaced boundary vertices don't dominate the
 * whole-AOI ground-raycast budget (issue #17). */
const DEFAULT_DECIMATE_M = 100;
/** Target drape edge length (m): fine enough to follow broad relief over a
 * wood, coarse enough to keep the whole-AOI raycast budget bounded. */
const DEFAULT_TARGET_EDGE_M = 110;
/** Hard cap on triangles produced for a single polygon's drape (issue #17): a
 * huge woodland drapes coarsely rather than exploding vertex / raycast count. */
const DEFAULT_MAX_TRIS_PER_POLYGON = 128;
/** Distance band (m) beyond the near radius over which a polygon's canopy
 * crossfades from hidden (0) to the full overview fill (1). */
const DEFAULT_FADE_BAND_M = 350;
/** Rebuild the merged mesh on camera movement only once the camera has moved
 * this far (m) — throttles crossfade rebuilds to the tile-update tick's budget
 * (the fade is smooth because the band is wide relative to this step). */
const REBUILD_MOVE_M = 30;

type Zone = 'hidden' | 'fading' | 'full';

interface CanopyPolygon {
  /** Draped vertices (local x/z); y resolved per vertex into groundY. */
  points: LocalXZ[];
  /** Triangle vertex-index triples into `points`. */
  tris: number[];
  bounds: RingBounds;
  style: CanopyClassStyle;
  /** Resolved ground height per point, or null until sampled. */
  groundY: Array<number | null>;
  /** Tile generation a point was last attempted at (dedupe within a gen). */
  attemptGen: number[];
  /** Count of still-ungrounded points in this polygon. */
  pending: number;
  /** Camera zone at the last rebuild (drives incremental rebuilds). */
  zone: Zone;
}

export interface CanopyLayerOptions {
  /** Near radius (m) shared/mirrored from VegetationLayer: within it the
   * instanced trees are the canopy, so the fill is hidden. */
  nearRadiusM?: number;
  /** Crossfade band width (m) beyond the near radius. */
  fadeBandM?: number;
  /** Drape target edge length (m). */
  targetEdgeM?: number;
  /** Per-polygon triangle cap for the drape. */
  maxTrisPerPolygon?: number;
  /** Boundary decimation spacing (m); 0 disables decimation. */
  decimateM?: number;
}

/** Farthest distance (m) from (cx,cz) to any corner of an axis-aligned bounds. */
function boundsFarDistance(b: RingBounds, cx: number, cz: number): number {
  const dx = Math.max(Math.abs(b.minX - cx), Math.abs(b.maxX - cx));
  const dz = Math.max(Math.abs(b.minZ - cz), Math.abs(b.maxZ - cz));
  return Math.hypot(dx, dz);
}

/** Nearest distance (m) from (cx,cz) to an axis-aligned bounds. */
function boundsNearDistance(b: RingBounds, cx: number, cz: number): number {
  const dx = Math.max(b.minX - cx, 0, cx - b.maxX);
  const dz = Math.max(b.minZ - cz, 0, cz - b.maxZ);
  return Math.hypot(dx, dz);
}

export class CanopyLayer {
  readonly group = new Group();
  private readonly material: MeshStandardMaterial;
  private readonly polygons: CanopyPolygon[] = [];
  private readonly nearRadius: number;
  private readonly fadeBand: number;
  private mesh?: Mesh;
  private pending: number;
  private fallbackGeneration = 0;
  private builtCamera: { x: number; z: number } | null = null;

  constructor(features: LandcoverFeature[], options: CanopyLayerOptions = {}) {
    this.group.name = 'vegetation-canopy';
    this.nearRadius = options.nearRadiusM ?? 0;
    this.fadeBand = options.fadeBandM ?? DEFAULT_FADE_BAND_M;
    const targetEdge = options.targetEdgeM ?? DEFAULT_TARGET_EDGE_M;
    const maxTris = options.maxTrisPerPolygon ?? DEFAULT_MAX_TRIS_PER_POLYGON;
    const decimate = options.decimateM ?? DEFAULT_DECIMATE_M;

    this.material = new MeshStandardMaterial({
      vertexColors: true,
      roughness: 1,
      metalness: 0,
      flatShading: true,
      side: DoubleSide,
      transparent: true,
      // Faded (near-camera) fragments must not punch depth holes in the terrain
      // behind them; the far, fully-opaque canopy still depth-tests correctly.
      depthWrite: false,
      // Small green emissive floor so DoubleSide undersides / sun-starved faces
      // read as dark woodland green rather than black under the weak ambient.
      emissive: new Color(0.03, 0.06, 0.035),
      emissiveIntensity: 1,
    });

    let pending = 0;
    for (const f of features) {
      const style = classCanopy(f.landcover_class);
      if (!style) continue; // tree_row: no fill surface
      const ring = simplifyRing(landcoverRingToLocal(f.geometry), decimate);
      // Drop the closing duplicate so it isn't grounded as an unused vertex.
      const verts =
        ring.length > 1 && ring[0].x === ring[ring.length - 1].x && ring[0].z === ring[ring.length - 1].z
          ? ring.slice(0, -1)
          : ring;
      const baseTris = triangulateXZ(verts);
      if (baseTris.length === 0) continue; // degenerate / non-simple: no fill
      const drape = drapePolygon(verts, baseTris, targetEdge, maxTris);
      if (drape.tris.length === 0) continue;
      this.polygons.push({
        points: drape.points,
        tris: drape.tris,
        bounds: ringBounds(verts),
        style,
        groundY: new Array<number | null>(drape.points.length).fill(null),
        attemptGen: new Array<number>(drape.points.length).fill(Number.NaN),
        pending: drape.points.length,
        zone: 'full',
      });
      pending += drape.points.length;
    }
    this.pending = pending;
  }

  /** Count of drape vertices whose ground height has not yet resolved. */
  get unresolvedCount(): number {
    return this.pending;
  }

  /** Number of canopy polygons that carry a fill surface. */
  get polygonCount(): number {
    return this.polygons.length;
  }

  /**
   * Grounds still-unresolved drape vertices (up to maxSamples raycasts this
   * call) and rebuilds the merged fill mesh when either new vertices grounded or
   * the camera crossed a near/fade/far boundary (so the crossfade tracks the
   * camera). Returns true if the sample budget ran out before every pending
   * vertex was attempted this generation (mirrors RoadWallsLayer.update).
   */
  update(
    sampleGround: GroundSampler,
    maxSamples = Infinity,
    generation?: number,
    camera?: { x: number; z: number },
  ): boolean {
    const gen = generation ?? (this.fallbackGeneration -= 1);
    let samples = 0;
    let grounded = false;
    let exhausted = false;

    if (this.pending > 0) {
      // Gather this generation's still-pending drape vertices across every
      // polygon, then spend the shared budget nearest-camera-first (issue #97) —
      // by actual per-vertex distance, across polygon boundaries — so a
      // contended tick grounds the canopy the camera is looking at before
      // distant fill. Ties break on (polygon index, vertex index) so the order
      // can't flicker tick-to-tick with a still camera. With no camera the walk
      // stays in file/vertex order. The pending array is bounded by the pending
      // count (shrinks as vertices ground), built on the tile-update tick.
      const pending: Array<{ p: CanopyPolygon; i: number; pi: number; d2: number }> = [];
      for (let pi = 0; pi < this.polygons.length; pi += 1) {
        const p = this.polygons[pi];
        if (p.pending === 0) continue;
        for (let i = 0; i < p.points.length; i += 1) {
          if (p.groundY[i] !== null || p.attemptGen[i] === gen) continue;
          let d2 = 0;
          if (camera) {
            const dx = p.points[i].x - camera.x;
            const dz = p.points[i].z - camera.z;
            d2 = dx * dx + dz * dz;
          }
          pending.push({ p, i, pi, d2 });
        }
      }
      if (camera) pending.sort((a, b) => a.d2 - b.d2 || a.pi - b.pi || a.i - b.i);

      for (const q of pending) {
        if (samples >= maxSamples) {
          exhausted = true;
          break;
        }
        samples += 1;
        q.p.attemptGen[q.i] = gen;
        const y = sampleGround(q.p.points[q.i].x, q.p.points[q.i].z);
        if (y !== null) {
          q.p.groundY[q.i] = y;
          q.p.pending -= 1;
          this.pending -= 1;
          grounded = true;
        }
      }
    }

    // Recompute each polygon's camera zone; note whether any changed.
    let zoneChanged = false;
    let anyFading = false;
    for (const p of this.polygons) {
      const z = this.zoneOf(p, camera);
      if (z !== p.zone) {
        p.zone = z;
        zoneChanged = true;
      }
      if (z === 'fading') anyFading = true;
    }

    const moved =
      camera !== undefined &&
      (this.builtCamera === null ||
        Math.hypot(camera.x - this.builtCamera.x, camera.z - this.builtCamera.z) >= REBUILD_MOVE_M);

    if (grounded || zoneChanged || (anyFading && moved)) {
      this.rebuild(camera);
      this.builtCamera = camera ? { x: camera.x, z: camera.z } : null;
    }
    return exhausted;
  }

  /** Camera zone for a polygon: hidden if it lies entirely within the near
   * radius (trees cover it), full if entirely beyond the fade band, else
   * fading. With no camera the whole overview shows (full). */
  private zoneOf(p: CanopyPolygon, camera?: { x: number; z: number }): Zone {
    if (!camera || this.nearRadius <= 0) return 'full';
    const far = boundsFarDistance(p.bounds, camera.x, camera.z);
    if (far <= this.nearRadius) return 'hidden';
    const near = boundsNearDistance(p.bounds, camera.x, camera.z);
    if (near >= this.nearRadius + this.fadeBand) return 'full';
    return 'fading';
  }

  private rebuild(camera?: { x: number; z: number }): void {
    const positions: number[] = [];
    const colors: number[] = [];
    const indices: number[] = [];

    for (const p of this.polygons) {
      if (p.zone === 'hidden') continue;
      const base = positions.length / 3;
      // Per-vertex alpha: 1 for 'full', distance crossfade for 'fading'.
      const alphaOf =
        p.zone === 'full' || !camera
          ? (): number => 1
          : (v: LocalXZ): number =>
              canopyFadeAlpha(Math.hypot(v.x - camera.x, v.z - camera.z), this.nearRadius, this.fadeBand);
      for (let i = 0; i < p.points.length; i += 1) {
        const v = p.points[i];
        const g = p.groundY[i];
        // Draped at treetop height: the per-vertex ground sample plus the class
        // canopy height. Ungrounded vertices sit at 0 with alpha 0; any triangle
        // touching one is skipped below, so their position never shows.
        positions.push(v.x, g === null ? 0 : g + p.style.heightM, v.z);
        colors.push(p.style.color[0], p.style.color[1], p.style.color[2], g === null ? 0 : alphaOf(v));
      }
      // Emit only triangles whose three vertices are all grounded (a large
      // polygon straddling the loaded-tile edge fills progressively).
      for (let t = 0; t < p.tris.length; t += 3) {
        const a = p.tris[t];
        const b = p.tris[t + 1];
        const c = p.tris[t + 2];
        if (p.groundY[a] === null || p.groundY[b] === null || p.groundY[c] === null) continue;
        indices.push(base + a, base + b, base + c);
      }
    }

    if (this.mesh) {
      this.group.remove(this.mesh);
      this.mesh.geometry.dispose();
      this.mesh = undefined;
    }
    if (indices.length === 0) return;

    const geo = new BufferGeometry();
    geo.setAttribute('position', new BufferAttribute(new Float32Array(positions), 3));
    // 4-component (RGBA) vertex colour: three.js reads the alpha channel for the
    // crossfade when the material is transparent.
    geo.setAttribute('color', new BufferAttribute(new Float32Array(colors), 4));
    geo.setIndex(indices);
    geo.computeVertexNormals();
    this.mesh = new Mesh(geo, this.material);
    // The canopy receives sun (flat-shaded tint) but does not cast — a big
    // shadow caster over the whole AOI is a needless cost and the near-camera
    // trees already cast (issue #17).
    this.mesh.castShadow = false;
    this.mesh.receiveShadow = true;
    this.group.add(this.mesh);
  }
}
